import {TargetTaskIdAndStorables} from 'inventory/check-in-check-out/BaseCheckOutFromProcessTableModal';
import CheckOutFromProcessTableModal from 'inventory/check-in-check-out/CheckOutFromProcessTableModal';
import ReserveFromProcessTableModal from 'inventory/check-in-check-out/ReserveFromProcessTableModal';
import {
  createInventoryActionHandlerFunction,
  InventoryAction,
  InventoryActionHandlerArgs,
  loadContainersFromCacheForUpdatingRestrictedSampleUsers,
  makeInventoryShortcutsActionFunction,
} from 'notebook/structured-tables/action-functions/inventoryShortcutsActionFunction';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {TableIndicatorStatus} from 'notebook/tables/TableConstants';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import ProcessOutputStructuredTableAdapter, {
  ProcessOutputStructuredTable,
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata,
} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import MoveStorablesFromProcessTableModal from 'samples/MoveStorablesFromProcessTableModal';
import BulkUpdateRestrictedSampleUsersFromProcessTableModal from 'samples/toolbar/BulkUpdateRestrictedSampleUsersFromProcessTableModal';
import {ItemWithSampleRolesAndPermissions} from 'samples/toolbar/BulkUpdateSampleRolesModal';

type ProcessTableInventoryActionHandlerArgs =
  | InventoryActionHandlerArgs<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata>
  | InventoryActionHandlerArgs<ProcessOutputStructuredTableMetadata, ProcessOutputStructuredTableRowMetadata>;

function isProcessOutputTableArgs(
  args: ProcessTableInventoryActionHandlerArgs
): args is InventoryActionHandlerArgs<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata
> {
  return args.structuredTable.kind === StructuredTableKind.PROCESS_OUTPUT;
}

function getOutputTableTargetTaskIdsAndStorables(
  args: InventoryActionHandlerArgs<
    ProcessOutputStructuredTableMetadata,
    ProcessOutputStructuredTableRowMetadata
  >
): Array<TargetTaskIdAndStorables> {
  const adapter = ProcessOutputStructuredTableAdapter.getAdapter();
  return args.targetRowsAndStorables.map(({targetStorables, structuredTableRow}) => ({
    targetStorables,
    taskId: (args.structuredTable.metadata.taskId ??
      adapter.getTaskId(args.structuredTable.columns, structuredTableRow))!,
  }));
}

function getTaskTableTargetTaskIdsAndStorables(
  args: InventoryActionHandlerArgs<ProcessTaskStructuredTableMetadata, ProcessTaskStructuredTableRowMetadata>
): Array<TargetTaskIdAndStorables> {
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();
  return args.targetRowsAndStorables.map(({targetStorables, structuredTableRow}) => ({
    targetStorables,
    taskId: adapter.getTaskId(args.structuredTable.columns, structuredTableRow)!,
  }));
}

function getTargetTaskIdsAndStorables(
  args: ProcessTableInventoryActionHandlerArgs
): Array<TargetTaskIdAndStorables> {
  if (isProcessOutputTableArgs(args)) {
    return getOutputTableTargetTaskIdsAndStorables(args);
  }
  return getTaskTableTargetTaskIdsAndStorables(args);
}

export function processTableCheckOutActionHandler(args: ProcessTableInventoryActionHandlerArgs): void {
  const targetTaskIdsAndStorables = getTargetTaskIdsAndStorables(args);
  createInventoryActionHandlerFunction(
    CheckOutFromProcessTableModal,
    (args) => ({
      targetTaskIdsAndStorables,
    }),
    {
      shouldAlwaysShowSampleStatusTable: true,
    }
  )(args);
}

export function processTableReserveActionHandler(args: ProcessTableInventoryActionHandlerArgs): void {
  const targetTaskIdsAndStorables = getTargetTaskIdsAndStorables(args);
  createInventoryActionHandlerFunction(
    ReserveFromProcessTableModal,
    (args) => ({targetTaskIdsAndStorables}),
    {
      shouldAlwaysShowSampleStatusTable: true,
    }
  )(args);
}

export function processTableUpdateRestrictedSampleUsersActionHandler(
  args: ProcessTableInventoryActionHandlerArgs
): void {
  const targetTaskIdsAndStorables = getTargetTaskIdsAndStorables(args);
  const containers = loadContainersFromCacheForUpdatingRestrictedSampleUsers(args.targetRowsAndStorables);
  createInventoryActionHandlerFunction(
    BulkUpdateRestrictedSampleUsersFromProcessTableModal,
    (args) => ({
      items: containers as Array<ItemWithSampleRolesAndPermissions>,
      targetTaskIdsAndStorables,
    }),
    {
      shouldAlwaysShowSampleStatusTable: true,
    }
  )(args);
}

export function processTableMoveStorablesActionHandler(
  args: InventoryActionHandlerArgs<unknown, unknown>
): void {
  createInventoryActionHandlerFunction(MoveStorablesFromProcessTableModal, undefined, {
    shouldAlwaysShowSampleStatusTable: true,
  })(args);
}

export function getForceEnableInventoryShortcuts(
  structuredTable: ProcessOutputStructuredTable | ProcessTaskStructuredTable,
  tableStatus?: TableIndicatorStatus
): boolean {
  const tableLocation = structuredTable.metadata.tableLocation;
  switch (tableLocation) {
    case ProcessTableLocation.DIRECT_EXECUTION_MODAL:
      return tableStatus !== TableIndicatorStatus.ERROR;
    default:
      return false;
  }
}

export const processTableInventoryShortcutsActionFunction = makeInventoryShortcutsActionFunction(
  {
    [InventoryAction.CHECK_OUT]: processTableCheckOutActionHandler,
    [InventoryAction.RESERVE]: processTableReserveActionHandler,
    [InventoryAction.UPDATE_RESTRICTED_SAMPLE_USERS]: processTableUpdateRestrictedSampleUsersActionHandler,
    [InventoryAction.MOVE_STORABLES]: processTableMoveStorablesActionHandler,
  },
  getForceEnableInventoryShortcuts,
  undefined,
  undefined
);
